import { useContext } from "react";
import PrivateLayout from "@/components/layout/auth/PrivateLayout";
import { AuthContext } from "@/lib/auth/models/auth.model";
import { useI18n } from "@/hooks/useI18n";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  AlertTriangle,
  CheckCircle2,
  CloudCog,
  Database,
  Loader2,
  RefreshCw,
  Users,
  XCircle,
} from "lucide-react";
import { useAdminStats, FunctionStats, AdminStatsResponse } from "@/lib/admin/hooks/useEdgeFunctionStats";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatBytes(bytes: number | null): string {
  if (bytes == null) return "—";
  if (bytes >= 1073741824) return `${(bytes / 1073741824).toFixed(1)} GB`;
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
}

function usagePercent(used: number | null, limit: number | null): number | null {
  if (used == null || limit == null || limit === 0) return null;
  return Math.min(Math.round((used / limit) * 100), 100);
}

function progressColor(pct: number | null): string {
  if (pct == null) return "bg-gray-300";
  if (pct > 90) return "bg-red-500";
  if (pct > 70) return "bg-yellow-400";
  return "bg-green-500";
}

// ─── Mail-processor alert ─────────────────────────────────────────────────────

function MailProcessorAlert({ functions }: { functions: FunctionStats[] }) {
  const { t } = useI18n();
  const mailStat = functions.find((s) => s.slug === "mail-processor");

  if (!mailStat || mailStat.total_calls === 0) return null;
  const errorRate = mailStat.error_calls / mailStat.total_calls;
  if (errorRate <= 0.5) return null;

  return (
    <Card className="border-red-200">
      <CardHeader className="flex flex-row items-center gap-3 pb-3">
        <div className="flex items-center justify-center h-9 w-9 rounded-lg bg-red-50 text-red-600 flex-shrink-0">
          <AlertTriangle className="h-5 w-5" />
        </div>
        <div className="flex flex-col flex-1 min-w-0">
          <CardTitle className="text-base font-semibold">
            {t("adminDashboard.mailProcessor.title")}
          </CardTitle>
          <span className="text-xs font-medium text-red-600 mt-0.5">
            {t("adminDashboard.mailProcessor.errorRateHigh")}
          </span>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between py-2 px-3 rounded-md bg-red-50">
          <span className="text-sm font-medium text-red-700">
            {t("adminDashboard.mailProcessor.successCalls")}
          </span>
          <span className="text-sm font-semibold text-red-700">
            {mailStat.success_calls} / {mailStat.total_calls}{" "}
            {t("adminDashboard.mailProcessor.calls")}
          </span>
        </div>
        <div className="flex items-center justify-between py-2 px-3 rounded-md bg-red-50">
          <span className="text-sm font-medium text-red-700">
            {t("adminDashboard.mailProcessor.errorCalls")}
          </span>
          <span className="text-sm font-semibold text-red-700">
            {mailStat.error_calls} / {mailStat.total_calls}{" "}
            {t("adminDashboard.mailProcessor.calls")}
          </span>
        </div>
        <p className="text-sm text-gray-600 px-1">
          {t("adminDashboard.mailProcessor.description")}
        </p>
      </CardContent>
    </Card>
  );
}

// ─── Edge function stats table ────────────────────────────────────────────────

function EdgeFunctionTable({ functions, periodDays }: { functions: FunctionStats[]; periodDays: number }) {
  const { t } = useI18n();

  return (
    <div className="space-y-2">
      <p className="text-xs text-gray-500">
        {t("adminDashboard.edgeFunctions.periodLabel", { days: String(periodDays) })}
      </p>
      <div className="overflow-x-auto rounded-md border border-gray-100">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
              <th className="py-2 px-3 text-left">
                {t("adminDashboard.edgeFunctions.function")}
              </th>
              <th className="py-2 px-3 text-right">
                {t("adminDashboard.edgeFunctions.totalCalls")}
              </th>
              <th className="py-2 px-3 text-right">
                {t("adminDashboard.edgeFunctions.successRate")}
              </th>
              <th className="py-2 px-3 text-right">
                {t("adminDashboard.edgeFunctions.avgMs")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {functions.map((fn) => {
              const successRate =
                fn.total_calls > 0
                  ? Math.round((fn.success_calls / fn.total_calls) * 1000) / 10
                  : 0;
              const isHealthy = successRate >= 80;
              const successColor = isHealthy ? "text-green-600" : "text-red-600";

              return (
                <tr key={fn.slug} className="hover:bg-gray-50 transition-colors">
                  <td className="py-2 px-3">
                    <div className="flex items-center gap-2">
                      {isHealthy ? (
                        <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
                      )}
                      <span className="font-mono text-gray-800">{fn.slug}</span>
                    </div>
                  </td>
                  <td className="py-2 px-3 text-right font-semibold text-gray-900">
                    {fn.total_calls.toLocaleString()}
                  </td>
                  <td className={`py-2 px-3 text-right font-semibold ${successColor}`}>
                    {successRate.toFixed(1)}%
                  </td>
                  <td className="py-2 px-3 text-right text-gray-600">
                    {fn.avg_execution_ms != null
                      ? `${fn.avg_execution_ms.toFixed(0)} ms`
                      : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Usage quota bars ─────────────────────────────────────────────────────────

interface UsageBarProps {
  label: string;
  used: number | null;
  limit: number | null;
  formatValue?: (v: number | null) => string;
}

function UsageBar({ label, used, limit, formatValue = formatBytes }: UsageBarProps) {
  const pct = usagePercent(used, limit);
  const barColor = progressColor(pct);
  const usedStr = formatValue(used);
  const limitStr = formatValue(limit);
  const pctStr = pct != null ? `${pct}%` : "—";

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium text-gray-700">{label}</span>
        <span className="text-gray-500 text-xs">
          {usedStr} / {limitStr} ({pctStr})
        </span>
      </div>
      {pct != null ? (
        <Progress
          value={pct}
          className="h-2 bg-gray-100"
          style={
            {
              "--progress-indicator-color": undefined,
            } as React.CSSProperties
          }
        >
          {/* Override indicator color via inline wrapper since Progress uses bg-primary */}
        </Progress>
      ) : (
        <div className="relative h-2 w-full overflow-hidden rounded-full bg-gray-100">
          <div className="h-full w-0 rounded-full bg-gray-300" />
        </div>
      )}
      {/* Colored bar overlay using Tailwind classes */}
      {pct != null && (
        <div className="relative -mt-2 h-2 w-full overflow-hidden rounded-full">
          <div
            className={`h-full rounded-full transition-all ${barColor}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
    </div>
  );
}

function QuotaCard({ usage }: { usage: AdminStatsResponse["usage"] }) {
  const { t } = useI18n();

  const formatNumber = (v: number | null) =>
    v != null ? v.toLocaleString() : "—";

  return (
    <Card>
      <CardHeader className="flex flex-row items-center gap-3 pb-3">
        <div className="flex items-center justify-center h-9 w-9 rounded-lg bg-purple-50 text-purple-600 flex-shrink-0">
          <Database className="h-5 w-5" />
        </div>
        <CardTitle className="text-base font-semibold">
          {t("adminDashboard.quota.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <UsageBar
          label={t("adminDashboard.quota.dbSize")}
          used={usage.db_size_bytes}
          limit={usage.db_size_limit_bytes}
        />
        <UsageBar
          label={t("adminDashboard.quota.storage")}
          used={usage.storage_size_bytes}
          limit={usage.storage_size_limit_bytes}
        />
        <UsageBar
          label={t("adminDashboard.quota.bandwidth")}
          used={usage.bandwidth_bytes}
          limit={usage.bandwidth_limit_bytes}
        />
        <UsageBar
          label={t("adminDashboard.quota.mau")}
          used={usage.monthly_active_users}
          limit={usage.mau_limit}
          formatValue={formatNumber}
        />
        <UsageBar
          label={t("adminDashboard.quota.invocations")}
          used={usage.func_invocations_month}
          limit={usage.func_invocations_limit}
          formatValue={formatNumber}
        />
      </CardContent>
    </Card>
  );
}

// ─── Database card ─────────────────────────────────────────────────────────────

function DatabaseCard({ db }: { db: AdminStatsResponse["db"] }) {
  const { t } = useI18n();

  return (
    <Card>
      <CardHeader className="flex flex-row items-center gap-3 pb-3">
        <div className="flex items-center justify-center h-9 w-9 rounded-lg bg-emerald-50 text-emerald-600 flex-shrink-0">
          <Users className="h-5 w-5" />
        </div>
        <CardTitle className="text-base font-semibold">
          {t("adminDashboard.database.title")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="divide-y divide-gray-100">
          <div className="flex items-center justify-between py-2">
            <dt className="text-sm text-gray-600">{t("adminDashboard.database.dbSize")}</dt>
            <dd className="text-sm font-semibold text-gray-900">{formatBytes(db.size_bytes)}</dd>
          </div>
          <div className="flex items-center justify-between py-2">
            <dt className="text-sm text-gray-600">{t("adminDashboard.database.totalUsers")}</dt>
            <dd className="text-sm font-semibold text-gray-900">{db.total_users.toLocaleString()}</dd>
          </div>
          <div className="flex items-center justify-between py-2">
            <dt className="text-sm text-gray-600">{t("adminDashboard.database.mau30d")}</dt>
            <dd className="text-sm font-semibold text-gray-900">{db.mau_30d.toLocaleString()}</dd>
          </div>
        </dl>
      </CardContent>
    </Card>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const AdminDashboard = () => {
  const authContext = useContext(AuthContext);
  if (!authContext) throw new Error("AdminDashboard must be used inside an AuthProvider");

  const { t } = useI18n();
  const { data, loading, error, refresh } = useAdminStats({ days: 7 });

  const functions = data?.edge_functions.functions ?? [];
  const periodDays = data?.edge_functions.period_days ?? 7;

  return (
    <PrivateLayout
      pageTitle={t("adminDashboard.pageTitle")}
      pageDescription={t("adminDashboard.pageDescription")}
      showBackButton={false}
      showCtaButton={false}
    >
      <div className="space-y-6">

        {/* Refresh button */}
        <div className="flex justify-end">
          <button
            onClick={() => refresh()}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium text-gray-600 border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            {t("adminDashboard.refresh")}
          </button>
        </div>

        {/* Mail-processor alert — shown only when error rate > 50% */}
        {!loading && functions.length > 0 && (
          <MailProcessorAlert functions={functions} />
        )}

        {/* Loading state */}
        {loading && (
          <Card>
            <CardContent>
              <div className="flex items-center gap-2 py-10 text-gray-500 justify-center">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span className="text-sm">{t("adminDashboard.loading")}</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Error state */}
        {!loading && error && (
          <Card>
            <CardContent>
              <div className="flex items-center gap-2 py-10 text-red-500 justify-center">
                <XCircle className="h-5 w-5" />
                <span className="text-sm">{error}</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Data loaded */}
        {!loading && !error && data && (
          <>
            {/* Card 1 — Edge Functions */}
            <Card>
              <CardHeader className="flex flex-row items-center gap-3 pb-3">
                <div className="flex items-center justify-center h-9 w-9 rounded-lg bg-blue-50 text-blue-600 flex-shrink-0">
                  <CloudCog className="h-5 w-5" />
                </div>
                <CardTitle className="text-base font-semibold">
                  {t("adminDashboard.edgeFunctions.title")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {functions.length === 0 ? (
                  <p className="text-sm text-gray-500 py-6 text-center">
                    {t("adminDashboard.edgeFunctions.noData")}
                  </p>
                ) : (
                  <EdgeFunctionTable functions={functions} periodDays={periodDays} />
                )}
              </CardContent>
            </Card>

            {/* Card 2 — Quota Piano */}
            <QuotaCard usage={data.usage} />

            {/* Card 3 — Database */}
            <DatabaseCard db={data.db} />
          </>
        )}

      </div>
    </PrivateLayout>
  );
};

export default AdminDashboard;
